/** What the portal has read, kept per week (and per athlete, and the team's lifts) for the session. */
import { useEffect, useSyncExternalStore } from "react";
import { ApiError, AuthError, api, type Me } from "../api/client.js";
import { addDays, mondayOf, todayString } from "./model/days.js";
import { buildLifts, type TeamLifts } from "./model/lifts.js";
import type { LoadData } from "./model/load.js";
import { applyNote, removeNote, sortNotes, type Note } from "./model/notes.js";
import type { AttentionFlag } from "./model/attention.js";
import type { HistoryWorkout, LiftSet, Meet, MeetChange, PublishedWeek, TeamSessions, TeamWeek, TrainingDay, WeekEntry, Workout } from "./model/types.js";
import { buildWeek, type WeekModel } from "./model/week.js";

type Entry =
  | { status: "loading"; promise: Promise<unknown> }
  | { status: "ready"; value: unknown; at: number; refreshing?: Promise<unknown> }
  | { status: "error"; error: unknown };

/** How long what was read counts as current; after that, a page showing it, or the tab coming back, reads it again in the background. */
export const STALE_MS = 60_000;

const entries = new Map<string, Entry>();
const listeners = new Set<() => void>();
let version = 0;
let generation = 0;
let onAuthError: () => void = () => {};

function notify() {
  version++;
  for (const l of listeners) l();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setAuthHandler(handler: () => void): void {
  onAuthError = handler;
}

/** Signing out forgets everything read as that person. */
export function clearAll(): void {
  generation++;
  entries.clear();
  knownMe = null;
  bootMe = null;
  notify();
}

/** What a write answered, in place of what was read, so the page doesn't reload to show it. */
export function put<T>(key: string, value: T): void {
  entries.set(key, { status: "ready", value, at: Date.now() });
  notify();
}

export function invalidate(...keys: string[]): void {
  let changed = false;
  for (const key of keys) changed = entries.delete(key) || changed;
  if (changed) notify();
}

/** Keeps showing what was read while it is read again: the next page or load that wants it refreshes it. */
export function revalidate(...keys: string[]): void {
  let changed = false;
  for (const key of keys) {
    const entry = entries.get(key);
    if (!entry) continue;
    changed = true;
    if (entry.status === "ready") entries.set(key, { status: "ready", value: entry.value, at: 0 });
    else entries.delete(key);
  }
  if (changed) notify();
}

/** Every cached read whose key starts so, revalidated. */
export function revalidatePrefix(prefix: string): void {
  revalidate(...[...entries.keys()].filter((k) => k.startsWith(prefix)));
}

/** Everything read so far, revalidated: what a reconnect catches up on (DESIGN §31.4). */
export function revalidateAll(): void {
  revalidate(...entries.keys());
}

export function load<T>(key: string, loader: () => Promise<T>): Promise<T> {
  const entry = entries.get(key);
  if (entry?.status === "ready") return Promise.resolve(entry.value as T);
  if (entry?.status === "loading") return entry.promise as Promise<T>;
  const gen = generation;
  const promise: Promise<T> = loader().then(
    (value) => {
      // An entry invalidated while it loaded is not written back with what was read before.
      if (gen === generation && entries.get(key) === pending) {
        entries.set(key, { status: "ready", value, at: Date.now() });
        notify();
      }
      return value;
    },
    (error: unknown) => {
      if (gen === generation && entries.get(key) === pending) {
        entries.set(key, { status: "error", error });
        notify();
      }
      if (error instanceof AuthError) onAuthError();
      throw error;
    },
  );
  const pending: Entry = { status: "loading", promise };
  entries.set(key, pending);
  return promise;
}

/** Reads a stale entry again while it keeps showing; a failure keeps the old value. Resolves to what it then holds. */
function refresh(key: string, loader: () => Promise<unknown>): Promise<unknown> {
  const entry = entries.get(key);
  if (entry?.status !== "ready") return Promise.resolve(undefined);
  if (entry.refreshing) return entry.refreshing;
  const gen = generation;
  const marked: Entry = { ...entry };
  marked.refreshing = loader().then(
    (value) => {
      if (gen !== generation || entries.get(key) !== marked) return value;
      entries.set(key, { status: "ready", value, at: Date.now() });
      notify();
      return value;
    },
    (error: unknown) => {
      if (error instanceof AuthError) onAuthError();
      if (gen !== generation || entries.get(key) !== marked) return entry.value;
      entries.set(key, { status: "ready", value: entry.value, at: Date.now() });
      return entry.value;
    },
  );
  entries.set(key, marked);
  return marked.refreshing;
}

/**
 * What a one-off read (the builder's) wants: the cached value while it is current, else read
 * again and wait for it. A failed re-read gives what was there.
 */
export function fresh<T>(key: string, loader: () => Promise<T>): Promise<T> {
  const entry = entries.get(key);
  if (entry?.status === "ready" && Date.now() - entry.at <= STALE_MS) return Promise.resolve(entry.value as T);
  if (entry?.status === "ready") return refresh(key, loader) as Promise<T>;
  return load(key, loader);
}

/** The value held for a key, if it has been read. */
export function peek<T>(key: string): T | undefined {
  const entry = entries.get(key);
  return entry?.status === "ready" ? (entry.value as T) : undefined;
}

if (typeof window !== "undefined") {
  const back = () => {
    if (document.visibilityState !== "hidden") notify();
  };
  window.addEventListener("focus", back);
  document.addEventListener("visibilitychange", back);
}

export interface Resource<T> {
  value: T | undefined;
  error: unknown;
  loading: boolean;
}

export function useResource<T>(key: string | null, loader: () => Promise<T>): Resource<T> {
  useSyncExternalStore(subscribe, () => version);
  useEffect(() => {
    if (!key) return;
    const entry = entries.get(key);
    if (!entry) load(key, loader).catch(() => {});
    else if (entry.status === "ready" && Date.now() - entry.at > STALE_MS) refresh(key, loader);
  });
  const entry = key ? entries.get(key) : undefined;
  return {
    value: entry?.status === "ready" ? (entry.value as T) : undefined,
    error: entry?.status === "error" ? entry.error : undefined,
    loading: !!key && (!entry || entry.status === "loading"),
  };
}

export function errorMessage(error: unknown): string {
  return error instanceof Error && error.message ? error.message : "Something went wrong. Try again.";
}

// MARK: The portal's reads.

export const weeksKey = "weeks";
export const weekKey = (monday: string) => "week:" + monday;
export const publishedKey = (monday: string) => "published:" + monday;
export const plansKey = "plans";
export const groupsKey = "groups";

const readWeeks = () =>
  api<{ weeks?: WeekEntry[] }>("/web/api/weeks").then(
    (r) => r?.weeks ?? [],
    (err: unknown) => {
      if (err instanceof AuthError) throw err;
      return [];
    },
  );

export function loadWeeks(): Promise<WeekEntry[]> {
  return load(weeksKey, readWeeks);
}

/** The weeks list for a one-off read: current, or read again. */
export function currentWeeks(): Promise<WeekEntry[]> {
  return fresh(weeksKey, readWeeks);
}

const readPublished = (monday: string) => () =>
  api<PublishedWeek>("/web/api/weeks/" + monday).catch((error: unknown) => {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  });

/** A published week as `GET /web/api/weeks/:monday` answers it (a week never published has no sessions), or null when there is none. */
export function loadPublished(monday: string): Promise<PublishedWeek | null> {
  return fresh(publishedKey(monday), readPublished(monday));
}

export function loadWeek(monday: string): Promise<WeekModel> {
  return load(weekKey(monday), async () => {
    const prev = addDays(monday, -7);
    // A weeks list already read says which weeks have nothing published, so those aren't asked for.
    const known = peek<WeekEntry[]>(weeksKey);
    const skip = (m: string) => !!known?.length && !known.some((w) => w.week === m) && peek(publishedKey(m)) === undefined;
    const fetchWeek = (m: string) => (skip(m) ? Promise.resolve(null) : loadPublished(m));
    const [weeks, week, sessions, thisWeek, prevWeek] = await Promise.all([
      loadWeeks(),
      api<TeamWeek>("/web/api/week?start=" + monday),
      api<TeamSessions>("/web/api/sessions?start=" + monday),
      fetchWeek(monday),
      fetchWeek(prev),
    ]);
    const entry = weeks.find((w) => w.week === monday);
    return buildWeek(monday, { week, sessions, thisWeek, prevWeek, publishedAt: entry?.updatedAt ?? null }, todayString());
  });
}

/** The weeks either side, read ahead once a page of this one has shown, so the arrows open at once. */
export function prefetchAround(monday: string): void {
  // A read ahead that fails is forgotten, so going to that week asks again rather than showing the failure.
  for (const m of [addDays(monday, -7), addDays(monday, 7)]) loadWeek(m).catch(() => invalidate(weekKey(m)));
}

/** After a week is published: what it changes, read again when next wanted. */
export function afterPublish(monday: string): void {
  invalidate(publishedKey(monday), weeksKey, plansKey);
  revalidate(weekKey(monday), weekKey(addDays(monday, 7)));
}

// MARK: Who is signed in

let bootMe: Promise<Me> | null = null;
let knownMe: Me | null = null;

const readMe = () => api<Me>("/web/api/me");

/** Asked in main.tsx before the router loads its first page, so the two travel together. */
export function startMe(): void {
  if (bootMe) return;
  bootMe = readMe();
  bootMe.catch(() => {});
}

/** The shell's /me: the one main.tsx started, the first time, else asked again. */
export function takeMe(): Promise<Me> {
  const promise = bootMe ?? readMe();
  bootMe = null;
  return promise.then((me) => {
    knownMe = me;
    return me;
  });
}

export function setKnownMe(me: Me): void {
  knownMe = me;
}

/** Who the shell is showing, once it knows. */
export function signedInMe(): Me | null {
  return knownMe;
}

/** Plan titles (`GET /web/api/plans`): only a coach who plans may ask, so anyone else has none. */
export function loadPlans<T>(reader: () => Promise<T[]>): Promise<T[]> {
  if (knownMe && !knownMe.person.canPlan) return Promise.resolve([]);
  return fresh(plansKey, reader);
}

export function loadGroups<T>(reader: () => Promise<T>): Promise<T> {
  return fresh(groupsKey, reader);
}

export const historyKey = (id: string) => "history:" + id;

export interface AthleteHistory {
  workouts: HistoryWorkout[];
}

export function loadHistory(id: string): Promise<AthleteHistory> {
  return load(historyKey(id), () =>
    api<{ workouts?: HistoryWorkout[] }>("/web/api/athletes/" + encodeURIComponent(id) + "/workouts").then((r) => {
      const workouts = r?.workouts ?? [];
      return { workouts };
    }),
  );
}

export const loadKey = "load";

/**
 * The last twelve weeks of everyone's load, up to this week; the same whichever page asks. The end is
 * sent because the Worker's own "this week" is UTC's, already next week on a Sunday evening here.
 */
export function loadLoad(): Promise<LoadData> {
  return load(loadKey, () => api<LoadData>("/web/api/load?weeks=12&end=" + mondayOf(todayString())).then((r) => ({ weeks: r?.weeks ?? [], athletes: r?.athletes ?? [] })));
}

export const liftsKey = "lifts";

export function loadLifts(): Promise<TeamLifts> {
  return load(liftsKey, () => api<{ sets?: LiftSet[] }>("/web/api/lifts").then((r) => buildLifts(r?.sets ?? [])));
}

export const notesKey = "notes";

/** Every note of mine, newest first (DESIGN §16.8). */
export function loadNotes(): Promise<Note[]> {
  return load(notesKey, () => api<{ notes?: Note[] }>("/web/api/notes").then((r) => sortNotes(r?.notes ?? [])));
}

function current(): Note[] | null {
  const entry = entries.get(notesKey);
  return entry?.status === "ready" ? (entry.value as Note[]) : null;
}

function written(change: (notes: Note[]) => Note[]): void {
  const notes = current();
  if (notes) put(notesKey, change(notes));
  else invalidate(notesKey);
}

export async function addNote(athleteID: string, body: string): Promise<Note> {
  const { note } = await api<{ note: Note }>("/web/api/notes", { method: "POST", body: { athleteID, body } });
  written((notes) => applyNote(notes, note));
  return note;
}

export async function changeNote(id: string, change: { body?: string; pinned?: boolean }): Promise<Note> {
  const { note } = await api<{ note: Note }>("/web/api/notes/" + encodeURIComponent(id), { method: "PATCH", body: change });
  written((notes) => applyNote(notes, note));
  return note;
}

export async function deleteNote(id: string): Promise<void> {
  await api("/web/api/notes/" + encodeURIComponent(id), { method: "DELETE" });
  written((notes) => removeNote(notes, id));
}

export const attentionKey = (monday: string) => "attention:" + monday;

/** The week's flags from athletes' notes (DESIGN §24.2); any failure but signing out reads as none. */
export function loadAttention(monday: string): Promise<AttentionFlag[]> {
  return load(attentionKey(monday), () =>
    api<{ flags?: AttentionFlag[] }>("/web/api/attention?start=" + monday).then(
      (r) => (Array.isArray(r?.flags) ? r.flags : []),
      (err: unknown) => {
        if (err instanceof AuthError) throw err;
        return [];
      },
    ),
  );
}

export const previousWorkoutsKey = (monday: string) => "previous-workouts:" + monday;

/** Everyone's workouts the week before `monday`, for Attention's running; null when it couldn't be read. */
export function loadPreviousWorkouts(monday: string): Promise<Workout[] | null> {
  const prev = addDays(monday, -7);
  const known = entries.get(weekKey(prev));
  if (known?.status === "ready") return Promise.resolve((known.value as WeekModel).workouts);
  return load(previousWorkoutsKey(monday), () =>
    api<TeamSessions>("/web/api/sessions?start=" + prev).then(
      (r) => r?.workouts ?? [],
      (err: unknown) => {
        if (err instanceof AuthError) throw err;
        return null;
      },
    ),
  );
}

export const meetsKey = (from: string, until: string) => "meets:" + from + ":" + until;

/** The team's meets overlapping `from`..`until` (DESIGN §27.3). */
export function loadMeets(from: string, until: string): Promise<Meet[]> {
  return load(meetsKey(from, until), () =>
    api<{ meets?: Meet[] }>("/web/api/meets?from=" + from + "&until=" + until).then((r) => (Array.isArray(r?.meets) ? r.meets : [])),
  );
}

function meetsChanged(): void {
  invalidate(...[...entries.keys()].filter((k) => k.startsWith("meets:")));
}

export async function addMeet(meet: { name: string; date: string; endDate?: string | null; place?: string | null }): Promise<Meet> {
  const { meet: made } = await api<{ meet: Meet }>("/web/api/meets", { method: "POST", body: meet });
  meetsChanged();
  return made;
}

export async function changeMeet(id: string, change: MeetChange): Promise<Meet> {
  const { meet } = await api<{ meet: Meet }>("/web/api/meets/" + encodeURIComponent(id), { method: "PATCH", body: change });
  meetsChanged();
  return meet;
}

export async function deleteMeet(id: string): Promise<void> {
  await api("/web/api/meets/" + encodeURIComponent(id), { method: "DELETE" });
  meetsChanged();
}

export const trainingKey = (date: string) => "training:" + date;

/** The team's day as it happens (DESIGN §41.2): who is training, who is done, and what each has logged. */
export function loadTraining(date: string): Promise<TrainingDay> {
  return load(trainingKey(date), () => readTraining(date));
}

/** The read itself, uncached: what `useResource` takes, so a revalidated day is asked for again rather than given back from the cache. */
export function readTraining(date: string): Promise<TrainingDay> {
  return api<TrainingDay>("/web/api/training?date=" + date).then((r) => ({
    date: r?.date ?? date,
    sessions: Array.isArray(r?.sessions) ? r.sessions : [],
    athletes: Array.isArray(r?.athletes) ? r.athletes : [],
    training: Array.isArray(r?.training) ? r.training : [],
    finished: Array.isArray(r?.finished) ? r.finished : [],
  }));
}

export interface Absence {
  athleteID: string;
  name: string;
  date: string;
  reason: string | null;
}

export const absencesPrefix = "absences:";
export const absencesKey = (from: string, to: string) => absencesPrefix + from + ":" + to;

/** Who said they won't be there, `from`..`to` (DESIGN §44.3). Any failure but signing out is none: the header just shows no one away. */
export function readAbsences(from: string, to: string): Promise<Absence[]> {
  return api<{ absences?: Absence[] }>("/web/api/absences?from=" + from + "&to=" + to).then(
    (r) => (Array.isArray(r?.absences) ? r.absences.filter((a) => a && typeof a.date === "string" && typeof a.name === "string") : []),
    (error: unknown) => {
      if (error instanceof AuthError) throw error;
      return [];
    },
  );
}

export interface SummaryItem {
  athleteID: string;
  name: string;
  text: string;
}

/** The coach's summary of a week (DESIGN §68.2). */
export interface WeekSummary {
  week: string;
  overview: string;
  holdBack: SummaryItem[];
  pushOn: SummaryItem[];
  catchUp: SummaryItem[];
  writtenAt: string;
}

export const summaryKey = (week: string) => "summary:" + week;

function summaryItems(list: unknown): SummaryItem[] {
  if (!Array.isArray(list)) return [];
  return list.filter(
    (i): i is SummaryItem => !!i && typeof i.athleteID === "string" && typeof i.name === "string" && typeof i.text === "string" && !!i.text.trim(),
  );
}

export function summaryFrom(raw: unknown, week: string): WeekSummary | null {
  if (!raw || typeof raw !== "object") return null;
  const s = raw as Partial<Record<keyof WeekSummary, unknown>>;
  return {
    week: typeof s.week === "string" ? s.week : week,
    overview: typeof s.overview === "string" ? s.overview.trim() : "",
    holdBack: summaryItems(s.holdBack),
    pushOn: summaryItems(s.pushOn),
    catchUp: summaryItems(s.catchUp),
    writtenAt: typeof s.writtenAt === "string" ? s.writtenAt : "",
  };
}

/** The stored summary of the week starting `week`, or null when there is none. */
export function loadSummary(week: string): Promise<WeekSummary | null> {
  return load(summaryKey(week), () =>
    api<{ summary?: unknown }>("/web/api/summary?week=" + week).then((r) => summaryFrom(r?.summary, week)),
  );
}

/** Writes the week's summary now, replacing the stored one (§68.2 "Writing it now"). */
export async function writeSummary(week: string): Promise<WeekSummary> {
  const r = await api<{ summary?: unknown }>("/web/api/summary/" + week + "/write", { method: "POST" });
  const summary = summaryFrom(r?.summary, week);
  if (!summary) throw new Error("Something went wrong. Try again.");
  put(summaryKey(week), summary);
  return summary;
}
